import { memo } from 'react';
import { AlertBanner, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { MailCheck } from 'lucide-react';

import InvitationShell from './InvitationShell';

/**
 * The invitation landing: workspace, who it is for, the optional personal welcome message and a
 * single confirm action. Presentational: what confirming does (sign in first, then accept) is
 * decided by the caller. No workspace content is rendered here.
 *
 * @param {object} props
 * @param {string} props.workspaceName
 * @param {string} [props.invitedEmail]
 * @param {string|null} [props.welcomeMessage] Shown only when the API returned one.
 * @param {() => void} props.onConfirm
 * @param {boolean} [props.confirming]
 * @param {boolean} [props.hasError] The last accept attempt failed.
 */
const AcceptInvitationScreen = ({
  workspaceName,
  invitedEmail,
  welcomeMessage,
  onConfirm,
  confirming = false,
  hasError = false,
}) => {
  const { t } = useTranslation('people-access');

  return (
    <InvitationShell>
      <MailCheck size={32} className="text-accent" aria-hidden />
      <div>
        <h1 className="m-0 text-xl font-semibold text-content">
          {t('invitation.accept.title', { workspace: workspaceName })}
        </h1>
        <p className="m-0 mt-1 text-sm text-content-muted">{t('invitation.accept.subtitle')}</p>
        {invitedEmail && (
          <p className="m-0 mt-1 text-xs text-content-muted">
            {t('invitation.accept.forEmail', { email: invitedEmail })}
          </p>
        )}
      </div>
      {welcomeMessage && (
        <figure className="m-0 rounded-lg border border-edge bg-surface-muted p-4">
          <figcaption className="text-xs font-medium text-content-muted">
            {t('invitation.accept.welcomeLabel')}
          </figcaption>
          <blockquote className="m-0 mt-1 text-sm text-content">{welcomeMessage}</blockquote>
        </figure>
      )}
      {hasError && <AlertBanner tone="danger">{t('invitation.accept.failed')}</AlertBanner>}
      <Button onClick={onConfirm} loading={confirming} disabled={confirming}>
        {confirming ? t('invitation.accept.confirming') : t('invitation.accept.confirm')}
      </Button>
    </InvitationShell>
  );
};

export default memo(AcceptInvitationScreen);
